import React, { useMemo, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Flame, RotateCcw, Droplets, AlertTriangle, ChevronDown, ChevronUp, Baby } from "lucide-react";

/*
  Body-diagram burns calculator
  =============================
  Mode "adult"  → Rule of Nines
  Mode "paed"   → Lund & Browder, age-adjusted

  Each clickable region cycles through: 0% → 25% → 50% → 75% → 100% burned (of that region).
  The region's CONTRIBUTION to total TBSA = (% burned) × (region's body-surface %).
*/

const CYCLE = [0, 0.25, 0.5, 0.75, 1];

/* Region body-surface %, SPLIT BY FRONT/BACK.
   For adult: Rule of 9s (totals = 100%).
   For paed : Lund & Browder, indexed by age-band column (total ~100%). */
// Keys: head, neck, torso_upper, torso_lower, upper_arm_L, upper_arm_R, forearm_L, forearm_R,
//        hand_L, hand_R, genitals, buttock_L, buttock_R, thigh_L, thigh_R, lower_leg_L, lower_leg_R, foot_L, foot_R
// Each region has a .front and .back fraction of its TOTAL body surface area.

// Lund-Browder TOTAL body-surface % by region, per age band index.
// Age bands: 0="0-1", 1="1-4", 2="5-9", 3="10-14", 4="15+"
// Source: SAMF / Burns SA / ATLS chart reference.
const LB = {
  head:        [19,   17,   13,   11,    9],
  neck:        [ 2,    2,    2,    2,    2],
  torso_upper: [13,   13,   13,   13,   13],  // whole upper trunk front+back, split below
  torso_lower: [13,   13,   13,   13,   13],
  upper_arm_L: [ 4,    4,    4,    4,    4],
  upper_arm_R: [ 4,    4,    4,    4,    4],
  forearm_L:   [ 3,    3,    3,    3,    3],
  forearm_R:   [ 3,    3,    3,    3,    3],
  hand_L:      [ 2.5,  2.5,  2.5,  2.5,  2.5],
  hand_R:      [ 2.5,  2.5,  2.5,  2.5,  2.5],
  genitals:    [ 1,    1,    1,    1,    1],
  buttock_L:   [ 2.5,  2.5,  2.5,  2.5,  2.5],
  buttock_R:   [ 2.5,  2.5,  2.5,  2.5,  2.5],
  thigh_L:     [ 5.5,  6.5,  8,    8.5,  9],
  thigh_R:     [ 5.5,  6.5,  8,    8.5,  9],
  lower_leg_L: [ 5,    5,    5.5,  6,    6.5],
  lower_leg_R: [ 5,    5,    5.5,  6,    6.5],
  foot_L:      [ 3.5,  3.5,  3.5,  3.5,  3.5],
  foot_R:      [ 3.5,  3.5,  3.5,  3.5,  3.5],
};

// Adult Rule of 9s (single column).
// torso_upper represents the whole upper trunk (front ant-trunk + back upper back),
// torso_lower the whole lower trunk — split 50/50 front/back below.
const ADULT = {
  head: 9, neck: 0, torso_upper: 18, torso_lower: 18,
  upper_arm_L: 4.5, upper_arm_R: 4.5, forearm_L: 2.75, forearm_R: 2.75,
  hand_L: 1.25, hand_R: 1.25, genitals: 1,
  buttock_L: 2.5, buttock_R: 2.5,
  thigh_L: 9, thigh_R: 9, lower_leg_L: 7, lower_leg_R: 7, foot_L: 3.5, foot_R: 3.5,
};

// Front/back split for each region (roughly — the model shows both sides separately).
// Each region's front% + back% should = 1.
const SIDE_SPLIT = {
  head: { front: 0.5, back: 0.5 },
  neck: { front: 0.5, back: 0.5 },
  torso_upper: { front: 0.5, back: 0.5 },
  torso_lower: { front: 0.5, back: 0.5 },
  upper_arm_L: { front: 0.5, back: 0.5 },
  upper_arm_R: { front: 0.5, back: 0.5 },
  forearm_L: { front: 0.5, back: 0.5 },
  forearm_R: { front: 0.5, back: 0.5 },
  hand_L: { front: 0.5, back: 0.5 },
  hand_R: { front: 0.5, back: 0.5 },
  genitals: { front: 1, back: 0 },
  buttock_L: { front: 0, back: 1 },
  buttock_R: { front: 0, back: 1 },
  thigh_L: { front: 0.5, back: 0.5 },
  thigh_R: { front: 0.5, back: 0.5 },
  lower_leg_L: { front: 0.5, back: 0.5 },
  lower_leg_R: { front: 0.5, back: 0.5 },
  foot_L: { front: 0.5, back: 0.5 },
  foot_R: { front: 0.5, back: 0.5 },
};

const AGE_BANDS = [
  { label: "0–1 year",  idx: 0 },
  { label: "1–4 years", idx: 1 },
  { label: "5–9 years", idx: 2 },
  { label: "10–14 years", idx: 3 },
  { label: "≥15 years",   idx: 4 },
];

// Helper: color intensity based on % burned
function regionFill(frac) {
  if (!frac) return "#ffffff";
  if (frac <= 0.25) return "#FECDD3";   // rose-200
  if (frac <= 0.5)  return "#FB923C";   // orange-400
  if (frac <= 0.75) return "#EF4444";   // red-500
  return "#991B1B";                      // red-900
}

function BodySide({ side, regions, onToggle }) {
  // Each "region" is a key like "head_front" / "head_back" / "torso_chest" / "torso_upper_back" ...
  // We render a stylised SVG body with clickable rectangles per region, labelled.
  const isFront = side === "front";
  const stroke = "#111827";
  const clickable = (id, label, d, cx, cy, fracKey) => {
    const frac = regions[fracKey] || 0;
    const fill = regionFill(frac);
    return (
      <g
        key={id}
        onClick={() => onToggle(fracKey)}
        className="cursor-pointer transition-all"
        data-testid={`burn-region-${fracKey}`}
      >
        <path d={d} fill={fill} stroke={stroke} strokeWidth="1.5" />
        {frac > 0 && (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize="8" fill={frac > 0.5 ? "white" : "#111827"} fontWeight="700" style={{ pointerEvents: "none" }}>
            {Math.round(frac * 100)}%
          </text>
        )}
        <title>{label} (click to cycle 0→100%)</title>
      </g>
    );
  };
  return (
    <svg viewBox="0 0 160 360" className="w-full h-auto max-h-[480px]" aria-label={`${side} body view`}>
      {/* head */}
      {clickable("head", "Head", "M80 8 Q95 8 95 23 Q95 40 80 44 Q65 40 65 23 Q65 8 80 8 Z", 80, 26, `head_${side}`)}
      {/* neck */}
      {clickable("neck", "Neck", "M72 44 L88 44 L88 54 L72 54 Z", 80, 49, `neck_${side}`)}
      {/* torso upper (chest / upper back) */}
      {clickable("torso_upper", isFront ? "Chest" : "Upper back", "M60 54 L100 54 L104 100 L56 100 Z", 80, 77, `torso_upper_${side}`)}
      {/* torso lower (abdomen / lower back) */}
      {clickable("torso_lower", isFront ? "Abdomen" : "Lower back", "M56 100 L104 100 L102 140 L58 140 Z", 80, 120, `torso_lower_${side}`)}
      {/* genitals / buttocks */}
      {isFront
        ? clickable("genitals", "Genitals", "M72 140 L88 140 L88 155 L72 155 Z", 80, 148, `genitals_${side}`)
        : (<>
            {clickable("buttock_L", "Left buttock", "M58 140 L80 140 L80 162 L62 162 Z", 69, 151, `buttock_L_${side}`)}
            {clickable("buttock_R", "Right buttock", "M80 140 L102 140 L98 162 L80 162 Z", 91, 151, `buttock_R_${side}`)}
          </>)
      }
      {/* Upper arms */}
      {clickable("upper_arm_L", "Left upper arm", "M46 60 L58 62 L56 100 L44 98 Z", 50, 80, `upper_arm_L_${side}`)}
      {clickable("upper_arm_R", "Right upper arm", "M102 62 L114 60 L116 98 L104 100 Z", 110, 80, `upper_arm_R_${side}`)}
      {/* Forearms */}
      {clickable("forearm_L", "Left forearm", "M44 100 L56 100 L55 136 L43 134 Z", 49, 118, `forearm_L_${side}`)}
      {clickable("forearm_R", "Right forearm", "M104 100 L116 100 L117 134 L105 136 Z", 110, 118, `forearm_R_${side}`)}
      {/* Hands */}
      {clickable("hand_L", "Left hand", "M43 136 L55 136 L55 156 L43 156 Z", 49, 146, `hand_L_${side}`)}
      {clickable("hand_R", "Right hand", "M105 136 L117 136 L117 156 L105 156 Z", 111, 146, `hand_R_${side}`)}
      {/* Thighs */}
      {clickable("thigh_L", "Left thigh", "M60 " + (isFront ? "155" : "162") + " L80 " + (isFront ? "155" : "162") + " L78 220 L62 220 Z", 70, (isFront ? 190 : 192), `thigh_L_${side}`)}
      {clickable("thigh_R", "Right thigh", "M80 " + (isFront ? "155" : "162") + " L100 " + (isFront ? "155" : "162") + " L98 220 L82 220 Z", 90, (isFront ? 190 : 192), `thigh_R_${side}`)}
      {/* Lower legs */}
      {clickable("lower_leg_L", "Left lower leg", "M62 220 L78 220 L77 290 L63 290 Z", 70, 255, `lower_leg_L_${side}`)}
      {clickable("lower_leg_R", "Right lower leg", "M82 220 L98 220 L97 290 L83 290 Z", 90, 255, `lower_leg_R_${side}`)}
      {/* Feet */}
      {clickable("foot_L", "Left foot", "M60 290 L80 290 L78 318 L58 316 Z", 70, 304, `foot_L_${side}`)}
      {clickable("foot_R", "Right foot", "M80 290 L100 290 L102 316 L82 318 Z", 90, 304, `foot_R_${side}`)}

      {/* View label */}
      <text x="80" y="344" textAnchor="middle" fontSize="11" fontWeight="700" fill="#111827">
        {isFront ? "FRONT" : "BACK"}
      </text>
    </svg>
  );
}

export default function BurnsDiagram({ mode = "adult" }) {
  // State key = `${region}_${side}` where side is "front" or "back"
  const [regions, setRegions] = useState({});
  const [weightKg, setWeightKg] = useState("");
  const [ageBand, setAgeBand] = useState(mode === "paed" ? "0–1 year" : "≥15 years");
  const [showChart, setShowChart] = useState(mode === "paed");

  const toggle = (key) => {
    setRegions((prev) => {
      const curr = prev[key] || 0;
      const next = CYCLE[(CYCLE.indexOf(curr) + 1) % CYCLE.length];
      return { ...prev, [key]: next };
    });
  };

  const reset = () => setRegions({});

  const ageIdx = useMemo(() => (AGE_BANDS.find((a) => a.label === ageBand)?.idx ?? 4), [ageBand]);

  const { tbsa, breakdown } = useMemo(() => {
    // For each region key, look up its total body % then multiply by its side split + user's % burned.
    const baseTable = mode === "paed"
      ? Object.fromEntries(Object.entries(LB).map(([k, v]) => [k, v[ageIdx]]))
      : ADULT;
    let sum = 0;
    const rows = [];
    for (const [region, basePct] of Object.entries(baseTable)) {
      const split = SIDE_SPLIT[region] || { front: 0.5, back: 0.5 };
      for (const side of ["front", "back"]) {
        const key = `${region}_${side}`;
        const frac = regions[key] || 0;
        const contrib = frac * basePct * split[side];
        if (contrib > 0) {
          sum += contrib;
          rows.push({ region: `${region.replace(/_/g, " ")} (${side})`, pct: contrib });
        }
      }
    }
    return { tbsa: sum, breakdown: rows };
  }, [regions, mode, ageIdx]);

  const w = parseFloat(weightKg || 0);
  const parklandMultiplier = mode === "paed" ? 3 : 4;  // Modified Parkland for kids (3), adult Parkland (4).
  const fluid24 = w && tbsa ? Math.round(parklandMultiplier * w * tbsa) : null;
  const fluid8 = fluid24 ? Math.round(fluid24 / 2) : null;

  // Holliday-Segar maintenance (paed only)
  let maintHourly = null;
  if (mode === "paed" && w) {
    if (w <= 10) maintHourly = 4 * w;
    else if (w <= 20) maintHourly = 40 + 2 * (w - 10);
    else maintHourly = 60 + 1 * (w - 20);
  }
  const maint24 = maintHourly ? Math.round(maintHourly * 24) : null;

  const severity =
    !tbsa ? null :
    tbsa >= 30 ? { label: "Severe burn", note: mode === "paed" ? "Admit to PICU / specialist burns unit. Transfer if not available." : "Admit ICU / specialist burns unit. Major resuscitation." } :
    tbsa >= 10 ? { label: "Moderate burn", note: "Admit, refer to burns service. Fluid resuscitation as below." } :
    tbsa >= 5  ? { label: "Minor to moderate burn", note: "Consider admission if face, hands, perineum, circumferential, inhalation, or child <5 y." } :
                 { label: "Small burn", note: "Likely outpatient dressings if superficial and not special-site." };

  return (
    <div className="space-y-5" data-testid={`burns-diagram-${mode}`}>
      <Card className="border-slate-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[160px]">
              <Label htmlFor="weight">Weight (kg)</Label>
              <Input
                id="weight"
                type="number"
                step="any"
                min={mode === "paed" ? 1 : 20}
                max={mode === "paed" ? 80 : 250}
                value={weightKg}
                onChange={(e) => setWeightKg(e.target.value)}
                className="mt-1.5"
                data-testid="burn-weight"
              />
            </div>
            {mode === "paed" && (
              <div className="flex-1 min-w-[180px]">
                <Label htmlFor="age_band">Age band</Label>
                <Select value={ageBand} onValueChange={setAgeBand}>
                  <SelectTrigger className="mt-1.5" data-testid="burn-age-band"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AGE_BANDS.map((a) => <SelectItem key={a.label} value={a.label}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button variant="outline" onClick={reset} size="sm" className="h-10" data-testid="burn-reset">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md p-3 leading-relaxed">
            <strong>Tap each body region to cycle</strong> through how much of it is burned: 0% → 25% → 50% → 75% → 100%. Tap <em>front</em> and <em>back</em> separately if the burn wraps around. Every tap updates the TBSA total live.
          </div>
          {mode === "paed" && (
            <div className="text-xs bg-[#EFF4FF]/50 border border-[#0B5FFF]/20 rounded-md p-3 leading-relaxed text-[#111827] flex items-start gap-2" data-testid="baby-proportions-note">
              <Baby className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Babies are not small adults.</strong> An infant's head accounts for about <strong>19% TBSA</strong> (vs 9% in an adult), and their legs make up proportionally less. Rule of Nines <strong>over-estimates</strong> burn extent in under-15s — this calculator uses the age-adjusted <strong>Lund &amp; Browder chart</strong> to correct for that. Change the age band above to see values shift live.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Body diagrams */}
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <BodySide side="front" regions={regions} onToggle={toggle} />
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <BodySide side="back" regions={regions} onToggle={toggle} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reference chart — how the age band changes the body-surface % per region */}
      {mode === "paed" && (
        <Card className="border-slate-200" data-testid="lb-reference-chart">
          <CardContent className="p-5">
            <button
              onClick={() => setShowChart((s) => !s)}
              className="w-full flex items-center justify-between text-left"
              data-testid="toggle-lb-chart"
            >
              <div>
                <div className="text-xs tracking-widest uppercase text-[#111827] font-semibold">Reference chart</div>
                <div className="font-heading font-semibold text-[#111827] text-sm mt-0.5">Lund &amp; Browder — current {ageBand}</div>
              </div>
              {showChart ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {showChart && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-3 py-2 font-semibold text-slate-700">Region</th>
                      {AGE_BANDS.map((a) => (
                        <th key={a.label} className={`text-right px-3 py-2 font-semibold ${a.label === ageBand ? "bg-[#0B5FFF] text-white" : "text-slate-700"}`}>
                          {a.label}
                        </th>
                      ))}
                      <th className="text-right px-3 py-2 font-semibold text-slate-700 border-l border-slate-200">Adult (9s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(LB).map(([k, arr]) => (
                      <tr key={k} className="border-b border-slate-100">
                        <td className="px-3 py-1.5 capitalize text-slate-700">{k.replace(/_/g, " ")}</td>
                        {arr.map((v, i) => (
                          <td key={i} className={`text-right px-3 py-1.5 font-mono ${AGE_BANDS[i].label === ageBand ? "bg-[#EFF4FF] text-[#111827] font-bold" : "text-slate-600"}`}>
                            {v}
                          </td>
                        ))}
                        <td className="text-right px-3 py-1.5 font-mono text-slate-500 border-l border-slate-200">{ADULT[k] ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                  Values are <strong>total body-surface %</strong> per region (combined front + back). The calculator splits 50/50 front/back (genitals front-only, buttocks back-only) so you can tap front or back separately. Sum of all regions ≈ 100% for each age band.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      <Card className="border-[#0B5FFF]/30 bg-gradient-to-br from-[#EFF4FF]/40 to-white">
        <CardContent className="p-5 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="p-4 rounded-lg bg-[#0B5FFF] text-white">
              <div className="text-[10px] tracking-widest uppercase text-white/70 flex items-center gap-1">
                <Flame className="w-3 h-3" /> Total TBSA
              </div>
              <div className="font-heading text-4xl font-extrabold mt-1">{tbsa.toFixed(1)}<span className="text-lg">%</span></div>
              <div className="text-xs text-white/80 mt-1">
                {mode === "paed" ? `Lund & Browder · ${ageBand}` : "Rule of Nines"}
              </div>
            </div>
            <div className="p-4 rounded-lg bg-white border border-slate-200">
              <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold flex items-center gap-1">
                <Droplets className="w-3 h-3" /> {mode === "paed" ? "Modified Parkland (3 mL × kg × %)" : "Parkland (4 mL × kg × %)"}
              </div>
              {fluid24 ? (
                <>
                  <div className="font-heading text-2xl font-bold text-[#111827] mt-1">{fluid24.toLocaleString()} mL / 24 h</div>
                  <div className="text-xs text-slate-600 mt-1">{fluid8?.toLocaleString()} mL in first 8 h · {fluid8?.toLocaleString()} mL over next 16 h (Ringer's lactate)</div>
                </>
              ) : (
                <div className="text-sm text-slate-500 mt-2">Enter weight & tap regions.</div>
              )}
            </div>
            <div className="p-4 rounded-lg bg-white border border-slate-200">
              <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">
                {mode === "paed" ? "Maintenance (Holliday-Segar)" : "Notes"}
              </div>
              {mode === "paed" ? (
                maint24 ? (
                  <>
                    <div className="font-heading text-2xl font-bold text-[#111827] mt-1">{maintHourly.toFixed(1)} mL / h</div>
                    <div className="text-xs text-slate-600 mt-1">{maint24.toLocaleString()} mL / 24 h maintenance — <em>add to</em> Parkland volume. Use 0.9% NaCl + 5% dextrose.</div>
                  </>
                ) : <div className="text-sm text-slate-500 mt-2">Enter weight.</div>
              ) : (
                <div className="text-sm text-slate-700 mt-2 leading-relaxed">Adults do <strong>not</strong> routinely require maintenance <em>on top of</em> Parkland. Titrate to urine output 0.5–1 mL/kg/h.</div>
              )}
            </div>
          </div>

          {severity && (
            <div className={`p-3 rounded-lg flex items-start gap-2 text-sm ${tbsa >= 10 ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-slate-50 border border-slate-200 text-slate-800"}`}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div><strong>{severity.label}.</strong> {severity.note} Titrate to urine output {mode === "paed" ? "(infants ≥1 mL/kg/h, older children 0.5–1 mL/kg/h)" : "0.5–1 mL/kg/h"} — Parkland is a starting estimate only.</div>
            </div>
          )}

          {breakdown.length > 0 && (
            <div>
              <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold mb-2">Regions contributing</div>
              <div className="flex flex-wrap gap-1.5">
                {breakdown.map((b, i) => (
                  <Badge key={i} variant="outline" className="text-xs capitalize">
                    {b.region} · {b.pct.toFixed(1)}%
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-500 border-t border-slate-200 pt-3 leading-relaxed">
            Decision support only. Re-assess burn depth and TBSA frequently; exclude superficial erythema (do not count). Consider airway, inhalation injury, escharotomy needs, analgesia, and tetanus status separately. Refer per your burns-service criteria.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
